import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Edit, Check, X, Loader2 } from 'lucide-react';
import { checkJournalistSubscription } from '@/services/stripeService';
import { toast } from "@/hooks/use-toast";
import { Journalist } from '@/hooks/journalist/types';

interface SubscriptionCardProps {
  name: string;
  subscription: {
    price: number;
    subscribers: number;
  };
  isOwnProfile: boolean;
  updateProfile: (updates: Partial<Journalist>) => Promise<void>;
}

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({ 
  name, 
  subscription, 
  isOwnProfile,
  updateProfile
}) => {
  const navigate = useNavigate(); // Add navigation
  const [editMode, setEditMode] = useState(false);
  const [price, setPrice] = useState(subscription.price.toString());
  const [isLoading, setIsLoading] = useState(false);
  const [hasSubscribed, setHasSubscribed] = useState(false);
  const { journalistId } = useParams<{ journalistId: string }>();
  
  useEffect(() => {
    // Check if the user is already subscribed to this journalist
    const checkSubscription = async () => {
      if (journalistId && !isOwnProfile) {
        const { hasAccess } = await checkJournalistSubscription(journalistId);
        setHasSubscribed(hasAccess);
      }
    };
    
    checkSubscription();
  }, [journalistId, isOwnProfile]);
  
  const handleSubscribe = async () => {
    try {
      if (!journalistId) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Unable to identify journalist profile.'
        });
        return;
      }
      
      // Navigate to Subscription page with a context about the journalist
      navigate('/subscription', { 
        state: { 
          journalistId, 
          initialPlan: 'individual',
          preselectedJournalist: name 
        } 
      });
    } catch (error) {
      console.error('Error navigating to subscription:', error);
      toast({
        variant: 'destructive',
        title: 'Subscription error',
        description: 'There was a problem processing your subscription. Please try again.'
      });
    }
  };

  const saveChanges = async () => {
    const numericPrice = parseFloat(price);
    if (isNaN(numericPrice) || numericPrice <= 0) {
      toast({
        variant: 'destructive',
        title: 'Invalid price',
        description: 'Please enter a valid price greater than zero.'
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      await updateProfile({
        subscription: {
          ...subscription,
          price: numericPrice
        }
      });
      
      // In a real implementation, we would also update the price in Stripe
      // await updateStripePrice(numericPrice);
      
      setEditMode(false);
      
      toast({
        title: 'Subscription price updated',
        description: `Your subscription price has been updated to $${numericPrice.toFixed(2)}/month.`
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Update failed',
        description: 'Failed to update subscription price. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const cancelChanges = () => {
    setPrice(subscription.price.toString());
    setEditMode(false);
  };
  
  return (
    <Card className="mb-8 border-2 border-primary/20 bg-primary/5">
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-xl font-bold mb-2">
              Get exclusive access to {name}'s premium content
            </h3>
            <p className="text-muted-foreground mb-4">
              Join {subscription.subscribers.toLocaleString()} subscribers who get early access, in-depth analysis, and exclusive content.
            </p>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-background">Premium Articles</Badge>
              <Badge variant="outline" className="bg-background">Direct Engagement</Badge>
              <Badge variant="outline" className="bg-background">Special Reports</Badge>
            </div>
          </div>
          <div className="flex flex-col items-center">
            {editMode ? (
              <div className="flex flex-col items-center gap-2">
                <div className="flex items-center">
                  <span className="text-xl mr-1">$</span>
                  <Input 
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    className="w-24 text-xl"
                    type="number"
                    step="0.01"
                    min="0.99"
                    disabled={isLoading}
                  />
                  <span className="text-lg font-normal text-muted-foreground ml-1">/month</span>
                </div>
                <div className="flex gap-2 mt-2">
                  <Button variant="outline" size="sm" onClick={cancelChanges} disabled={isLoading}>
                    <X className="h-4 w-4 mr-1" />
                    Cancel
                  </Button>
                  <Button size="sm" onClick={saveChanges} disabled={isLoading}>
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4 mr-1" />
                    )}
                    Save
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="text-3xl font-bold mb-2">${subscription.price.toFixed(2)}<span className="text-lg font-normal text-muted-foreground">/month</span></div>
                {isOwnProfile ? (
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setEditMode(true)}
                    className="w-full"
                    disabled={isLoading}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit Price
                  </Button>
                ) : (
                  hasSubscribed ? (
                    <Button 
                      size="lg"
                      variant="secondary"
                      className="w-full"
                      disabled
                    >
                      Currently Subscribed
                    </Button>
                  ) : (
                    <Button 
                      size="lg" 
                      className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                      onClick={handleSubscribe}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      ) : null}
                      Subscribe Now
                    </Button>
                  )
                )}
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SubscriptionCard;
